import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const PickList = ({ title, items, picked, toggle, prefix }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</p>
    <div className="space-y-1 max-h-48 overflow-y-auto">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
          <Checkbox checked={picked.has(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`${prefix}-${it.id}`} />
          {it.label}
        </label>
      ))}
    </div>
  </div>
);

export const AssignLessonDialog = ({ lesson, open, onOpenChange, onAssigned }) => {
  const { api, user } = useAuth();
  const [children, setChildren] = useState([]);
  const [classes, setClasses] = useState([]);
  const [kids, setKids] = useState(new Set());
  const [cls, setCls] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKids(new Set()); setCls(new Set());
    api.get('/children').then((r) => setChildren((r.data || []).map((c) => ({ id: c.id, label: c.name })))).catch(() => {});
    if (user?.organization_id) {
      api.get('/classes').then((r) => setClasses((r.data || []).map((c) => ({ id: c.id, label: `${c.name} (${c.student_count || 0} students)` })))).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggler = (setter) => (id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const assign = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/lessons/item/${lesson.id}/assign`, { child_ids: [...kids], class_ids: [...cls] });
      toast.success(`Assigned to ${r.data.assigned} learner${r.data.assigned === 1 ? '' : 's'}`);
      onAssigned?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lesson-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign “{lesson?.title}”</DialogTitle>
          <DialogDescription>It will show up on each learner's Learn page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {children.length > 0 && <PickList title="Children" items={children} picked={kids} toggle={toggler(setKids)} prefix="assign-child" />}
          {classes.length > 0 && <PickList title="Classes" items={classes} picked={cls} toggle={toggler(setCls)} prefix="assign-class" />}
          {!children.length && !classes.length && <p className="text-sm text-slate-500">Add a child profile first to assign lessons.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={assign} disabled={saving || (!kids.size && !cls.size)} className="bg-teal-600 hover:bg-teal-700" data-testid="assign-lesson-confirm-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignLessonDialog;
